import React from 'react';
import { styled } from '@withjoy/joykit';
import { safeInvoke } from '@shared/utils/safeInvoke';
import { animationTransitionExt } from '@shared/utils/animationTransition';
import { PreviewOption } from '@apps/admin/routes/WebsiteDesignerV2/WebsiteDesigner.types';

export interface PreviewToggleSvgProps {
  active: boolean;
  className?: string;
  color?: string;
  onClick: (e: React.MouseEvent<SVGSVGElement>, previewOption: PreviewOption) => void;
  previewOption: PreviewOption;
}

const styledSvgAnimation = (opacityDelay?: string) =>
  animationTransitionExt({ property: 'fill' }, { property: 'stroke' }, { property: 'color' }, { property: 'opacity', delay: opacityDelay });

export const TOGGLES_ANIMATION_STEP = 0.25;
export const FIRST_TOGGLE_ANIMATION_DELAY = 0.5 + TOGGLES_ANIMATION_STEP;
export const SECOND_TOGGLE_ANIMATION_DELAY = FIRST_TOGGLE_ANIMATION_DELAY + TOGGLES_ANIMATION_STEP;
export const LAST_TOGGLE_ANIMATION_DELAY = SECOND_TOGGLE_ANIMATION_DELAY + TOGGLES_ANIMATION_STEP;

const StyledSvg = styled.svg`
  outline: none;
  cursor: pointer;
  transition: ${styledSvgAnimation()};
  :hover {
    color: #7139bf;
  }
  &.animated {
    opacity: 0;
    &.mounted-0,
    &.mounted-1,
    &.mounted-2 {
      opacity: 1;
    }
    &.mounted-0 {
      transition: ${styledSvgAnimation(`${FIRST_TOGGLE_ANIMATION_DELAY}s`)};
    }
    &.mounted-1 {
      transition: ${styledSvgAnimation(`${SECOND_TOGGLE_ANIMATION_DELAY}s`)};
    }
    &.mounted-2 {
      transition: ${styledSvgAnimation(`${LAST_TOGGLE_ANIMATION_DELAY}s`)};
    }
  }
`;

// Disabled: "#BEBEBE"
// Active: #333333

const PreviewToggleWrapper: React.FC<PreviewToggleSvgProps & { a11yLabel: string }> = ({ a11yLabel, className, color = '#949494', active, children, onClick, previewOption }) => {
  const handleClick = (e: React.MouseEvent<SVGSVGElement>) => {
    safeInvoke(onClick, e, previewOption);
  };
  return (
    <StyledSvg
      tabIndex={active ? -1 : 0}
      onClick={handleClick}
      aria-label={`Preview ${a11yLabel}`}
      className={className}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      color={active ? '#333333' : color}
      xmlns="http://www.w3.org/2000/svg"
    >
      {children}
    </StyledSvg>
  );
};

PreviewToggleWrapper.displayName = 'PreviewToggleSVG';

const DesktopSvg: React.FC<PreviewToggleSvgProps> = props => {
  return (
    <PreviewToggleWrapper
      {...props}
      a11yLabel="desktop"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M21 3.2H3C2.55817 3.2 2.2 3.55817 2.2 4V16C2.2 16.4418 2.55817 16.8 3 16.8H21C21.4418 16.8 21.8 16.4418 21.8 16V4C21.8 3.55817 21.4418 3.2 21 3.2ZM3 2C1.89543 2 1 2.89543 1 4V16C1 17.1046 1.89543 18 3 18H21C22.1046 18 23 17.1046 23 16V4C23 2.89543 22.1046 2 21 2H3Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.90848 21.3656L8.99989 17L10.1641 17.291L9.07265 21.6567L7.90848 21.3656Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16.0915 21.3656L15.0001 17L13.8359 17.291L14.9273 21.6567L16.0915 21.3656Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7 21.4C7 21.0687 7.26863 20.8 7.6 20.8H16.4C16.7314 20.8 17 21.0687 17 21.4C17 21.7314 16.7314 22 16.4 22H7.6C7.26863 22 7 21.7314 7 21.4Z"
        fill="currentColor"
      />
    </PreviewToggleWrapper>
  );
};

const PhoneSvg: React.FC<PreviewToggleSvgProps> = props => {
  return (
    <PreviewToggleWrapper
      {...props}
      a11yLabel="mobile"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16 2.2H8C7.55817 2.2 7.2 2.55817 7.2 3V21C7.2 21.4418 7.55817 21.8 8 21.8H16C16.4418 21.8 16.8 21.4418 16.8 21V3C16.8 2.55817 16.4418 2.2 16 2.2ZM8 1C6.89543 1 6 1.89543 6 3V21C6 22.1046 6.89543 23 8 23H16C17.1046 23 18 22.1046 18 21V3C18 1.89543 17.1046 1 16 1H8Z"
        fill="currentColor"
      />
      <path
        d="M10.75 3.5C10.75 3.22386 10.9739 3 11.25 3H12.75C13.0261 3 13.25 3.22386 13.25 3.5V3.5C13.25 3.77614 13.0261 4 12.75 4H11.25C10.9739 4 10.75 3.77614 10.75 3.5V3.5Z"
        fill="currentColor"
      />
    </PreviewToggleWrapper>
  );
};

const StationerySvg: React.FC<PreviewToggleSvgProps> = props => {
  return (
    <PreviewToggleWrapper
      {...props}
      a11yLabel="matching stationery"
    >
      <path
        d="M2.6208 8.16978L2.73456 8.75889L2.73456 8.75889L2.6208 8.16978ZM19.3792 8.16978L19.2654 8.75889L19.2654 8.75889L19.3792 8.16978ZM18.0796 22.2166C18.3028 22.4616 18.6823 22.4792 18.9272 22.256C19.1721 22.0328 19.1898 21.6533 18.9666 21.4084L18.0796 22.2166ZM12.1399 14.8074L12.5833 14.4033C12.5771 14.3964 12.5706 14.3897 12.5641 14.3831L12.1399 14.8074ZM9.83064 14.8074L9.40645 14.3831C9.40263 14.3869 9.39886 14.3907 9.39514 14.3947L9.83064 14.8074ZM2.75634 21.3998C2.52839 21.6403 2.53859 22.0201 2.77911 22.248C3.01963 22.4759 3.39939 22.4657 3.62733 22.2252L2.75634 21.3998ZM7.687 17.0232C7.95871 17.2129 8.33274 17.1464 8.52243 16.8747C8.71212 16.603 8.64562 16.229 8.37392 16.0393L7.687 17.0232ZM1.59345 11.3057C1.32174 11.116 0.947708 11.1825 0.758021 11.4542C0.568335 11.7259 0.634827 12.0999 0.906536 12.2896L1.59345 11.3057ZM13.6261 16.0393C13.3544 16.229 13.2879 16.603 13.4776 16.8747C13.6672 17.1464 14.0413 17.2129 14.313 17.0232L13.6261 16.0393ZM21.0935 12.2896C21.3652 12.0999 21.4317 11.7259 21.242 11.4542C21.0523 11.1825 20.6782 11.116 20.4065 11.3057L21.0935 12.2896ZM20.4 10.1335V20H21.6V10.1335H20.4ZM19 21.4H3V22.6H19V21.4ZM1.6 20V10.1335H0.4V20H1.6ZM2.73456 8.75889L3.61376 8.58912L3.38624 7.41088L2.50704 7.58066L2.73456 8.75889ZM18.3862 8.58912L19.2654 8.75889L19.493 7.58066L18.6138 7.41088L18.3862 8.58912ZM3 21.4C2.2268 21.4 1.6 20.7732 1.6 20H0.4C0.4 21.4359 1.56406 22.6 3 22.6V21.4ZM20.4 20C20.4 20.7732 19.7732 21.4 19 21.4V22.6C20.4359 22.6 21.6 21.4359 21.6 20H20.4ZM21.6 10.1335C21.6 8.88763 20.7162 7.81688 19.493 7.58066L19.2654 8.75889C19.9241 8.88609 20.4 9.46265 20.4 10.1335H21.6ZM1.6 10.1335C1.6 9.46265 2.07588 8.88609 2.73456 8.75889L2.50704 7.58066C1.28377 7.81688 0.4 8.88763 0.4 10.1335H1.6ZM4.5 2.1H17.5V0.9H4.5V2.1ZM18.4 3V13.0312H19.6V3H18.4ZM3.6 13.0312V3H2.4V13.0312H3.6ZM17.5 2.1C17.9971 2.1 18.4 2.50294 18.4 3H19.6C19.6 1.8402 18.6598 0.9 17.5 0.9V2.1ZM4.5 0.9C3.3402 0.9 2.4 1.8402 2.4 3H3.6C3.6 2.50294 4.00294 2.1 4.5 2.1V0.9ZM18.9666 21.4084L12.5833 14.4033L11.6964 15.2115L18.0796 22.2166L18.9666 21.4084ZM12.5641 14.3831C12.1431 13.9622 11.5784 13.7222 10.9852 13.7222V14.9222C11.2514 14.9222 11.5134 15.0295 11.7156 15.2317L12.5641 14.3831ZM10.9852 13.7222C10.392 13.7222 9.8274 13.9622 9.40645 14.3831L10.2548 15.2317C10.4571 15.0295 10.7191 14.9222 10.9852 14.9222V13.7222ZM9.39514 14.3947L2.75634 21.3998L3.62733 22.2252L10.2661 15.2201L9.39514 14.3947ZM8.37392 16.0393L1.59345 11.3057L0.906536 12.2896L7.687 17.0232L8.37392 16.0393ZM14.313 17.0232L21.0935 12.2896L20.4065 11.3057L13.6261 16.0393L14.313 17.0232Z"
        fill="currentColor"
      />
    </PreviewToggleWrapper>
  );
};

export const previewToggleSvgs: { [previewOption in PreviewOption]: React.FC<PreviewToggleSvgProps> } = {
  [PreviewOption.DESKTOP]: DesktopSvg,
  [PreviewOption.MOBILE]: PhoneSvg,
  [PreviewOption.STATIONERY]: StationerySvg,
};
